import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import PhoneInput from "react-phone-number-input/input";
import ClientAutocomplete from "./ClientAutocomplete.tsx";
import type { ClientSummary } from "../pages/models";

type ClientOption = ClientSummary & {
  client_name?: string;
};

type FormValues = {
  client?: ClientOption | null | string;
  client_name?: string;
  email?: string;
  phone_number?: string;
};

type FormErrors = Record<string, unknown>;
type FormTouched = Record<string, unknown>;

type Props = {
  values: FormValues;
  errors?: FormErrors;
  touched?: FormTouched;
  setFieldValue: (field: string, value: unknown) => void;
  setFieldTouched?: (
    field: string,
    touched?: boolean,
    shouldValidate?: boolean,
  ) => void | Promise<unknown>;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur?: (event: React.FocusEvent<HTMLElement>) => void;
  isFormDisabled?: boolean;
  showAutocomplete?: boolean;
  showDetailsHeading?: boolean;
  initialClientLocked?: boolean;
  selectHeading?: string;
  detailsHeadingSelected?: string;
  detailsHeadingNew?: string;
  autocompletePlaceholder?: string;
  unlockLabel?: string;
};

const getClientPhoneNumber = (client?: ClientOption | null) =>
  client?.phoneNumber || client?.phone_number || "";

const getClientName = (client?: ClientOption | null) =>
  client?.clientName || client?.client_name || "";

const getClientValue = (
  client: FormValues["client"],
): ClientOption | null => {
  if (client && typeof client === "object") {
    return client;
  }

  return null;
};

const ClientSelectionFields = ({
  values,
  errors = {},
  touched = {},
  setFieldValue,
  setFieldTouched,
  handleChange,
  handleBlur,
  isFormDisabled = false,
  showAutocomplete = true,
  showDetailsHeading = true,
  initialClientLocked = false,
  selectHeading = "Select client",
  detailsHeadingSelected = "Client details",
  detailsHeadingNew = "or add new one",
  autocompletePlaceholder = "Select client",
  unlockLabel = "Unlock client fields to edit",
}: Props) => {
  const [isClientLocked, setIsClientLocked] = useState(initialClientLocked);
  const selectedClient = getClientValue(values.client);
  const clientNameError = typeof errors.client_name === "string" ? errors.client_name : null;
  const emailError = typeof errors.email === "string" ? errors.email : null;
  const phoneNumberError =
    typeof errors.phone_number === "string" ? errors.phone_number : null;
  const clientNameTouched = Boolean(touched.client_name);
  const emailTouched = Boolean(touched.email);
  const phoneNumberTouched = Boolean(touched.phone_number);

  useEffect(() => {
    setIsClientLocked(initialClientLocked);
  }, [initialClientLocked]);

  const handleClientChange = (client: ClientOption | null) => {
    if (client) {
      setFieldValue("email", client.email || "");
      setFieldValue("client_name", getClientName(client));
      setFieldValue("phone_number", getClientPhoneNumber(client));
      setFieldValue("client", client);
      setIsClientLocked(true);
      return;
    }

    setFieldValue("email", "");
    setFieldValue("client_name", "");
    setFieldValue("phone_number", "");
    setFieldValue("client", null);
    setIsClientLocked(false);
  };

  const fieldsDisabled = isFormDisabled || isClientLocked;

  return (
    <>
      {showAutocomplete && (
        <>
          <h3 className="h5 mb-3">{selectHeading}</h3>
          <ClientAutocomplete
            name="client"
            placeholder={autocompletePlaceholder}
            isDisabled={isFormDisabled}
            value={selectedClient}
            onChange={handleClientChange}
            onBlur={() => {
              if (setFieldTouched) {
                setFieldTouched("client", true);
              }
            }}
          />
        </>
      )}

      {showDetailsHeading && (
        <h3 className="h5 mb-3">
          {values.client ? detailsHeadingSelected : detailsHeadingNew}
        </h3>
      )}

      {isClientLocked && !isFormDisabled && (
        <Button
          variant="outline-warning"
          size="sm"
          onClick={() => setIsClientLocked(false)}
          style={{ marginBottom: "10px", width: "fit-content" }}
        >
          {unlockLabel}
        </Button>
      )}

      <Form.Group className="mb-2">
        <Form.Label>Name</Form.Label>
        <Form.Control
          type="text"
          name="client_name"
          value={values.client_name || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={fieldsDisabled}
        />
        {clientNameError && clientNameTouched && (
          <div className="text-danger small">{clientNameError}</div>
        )}
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label>Email</Form.Label>
        <Form.Control
          type="email"
          name="email"
          value={values.email || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={fieldsDisabled}
        />
        {emailError && emailTouched && (
          <div className="text-danger small">{emailError}</div>
        )}
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label>Phone number</Form.Label>
        <PhoneInput
          country="US"
          type="tel"
          name="phone_number"
          onChange={(value) => {
            setFieldValue("phone_number", value || "");
          }}
          onBlur={() => {
            if (setFieldTouched) {
              setFieldTouched("phone_number", true);
            }
          }}
          value={values.phone_number || ""}
          disabled={fieldsDisabled}
          className="form-control"
        />
        {phoneNumberError && phoneNumberTouched && (
          <div className="text-danger small">{phoneNumberError}</div>
        )}
      </Form.Group>
    </>
  );
};

export default ClientSelectionFields;
